import React, { useMemo, useState } from "react";
import axios from "axios";
import {
    Alert,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { getApiBaseUrl } from "../../../config/api";
import { formatConflictSummary, WEEK_DAYS } from "./timetableUtils";

export function TimetableConflictsPanel({ conflicts, classConflictKeys = [] }) {
    const conflictSet = useMemo(() => new Set(classConflictKeys), [classConflictKeys]);

    if (!conflicts?.total) {
        return (
            <Alert severity="success" sx={{ borderRadius: 2 }}>
                No teacher or room conflicts detected across all class timetables for this level and year.
            </Alert>
        );
    }

    const rows = [
        ...(conflicts.teacherConflicts || []).map((item) => ({ ...item, kind: "Teacher" })),
        ...(conflicts.roomConflicts || []).map((item) => ({ ...item, kind: "Room" })),
        ...(conflicts.bookingConflicts || []).map((item) => ({
            kind: "Booking",
            day: item.booking?.dayOfWeek,
            periodKey: item.booking?.periodKey,
            label: item.booking?.title || item.booking?.facilityName || "Booking",
            slots: item.slots || [],
        })),
    ];

    return (
        <Stack spacing={2}>
            <Alert severity="warning" icon={<WarningAmberOutlinedIcon />} sx={{ borderRadius: 2 }}>
                {formatConflictSummary(conflicts)}. Resolve clashes before publishing timetables.
            </Alert>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Type</TableCell>
                            <TableCell>Day</TableCell>
                            <TableCell>Period</TableCell>
                            <TableCell>Detail</TableCell>
                            <TableCell>Classes involved</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row, index) => {
                            const involved = (row.slots || [])
                                .map((slot) => slot.className)
                                .filter(Boolean)
                                .join(", ");
                            const highlightsCurrent =
                                row.slots?.some((slot) => conflictSet.has(`${slot.periodKey}|${slot.day}`)) || false;
                            return (
                                <TableRow key={`${row.kind}-${index}`} selected={highlightsCurrent}>
                                    <TableCell>
                                        <Chip size="small" label={row.kind} color={row.kind === "Teacher" ? "error" : "warning"} />
                                    </TableCell>
                                    <TableCell>{row.day}</TableCell>
                                    <TableCell>{row.periodLabel || row.periodKey}</TableCell>
                                    <TableCell>
                                        {row.kind === "Teacher"
                                            ? row.teacherName || row.teacherId
                                            : row.kind === "Room"
                                              ? row.room || row.facilityId
                                              : row.label}
                                    </TableCell>
                                    <TableCell>{involved || "—"}</TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Stack>
    );
}

const emptySubForm = {
    classId: "",
    className: "",
    periodKey: "",
    dayOfWeek: "Monday",
    effectiveDate: "",
    originalTeacherId: "",
    originalTeacherName: "",
    substituteTeacherId: "",
    subjectName: "",
    facilityId: "",
    roomName: "",
    notes: "",
};

export function TimetableSubstitutionsPanel({
    schoolId,
    educationLevel,
    academicYear,
    substitutions = [],
    classOptions = [],
    teacherOptions = [],
    facilityOptions = [],
    periodOptions = [],
    onRefresh,
}) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(emptySubForm);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const openCreate = () => {
        setForm({ ...emptySubForm, classId: classOptions[0]?.id || "" });
        setError("");
        setOpen(true);
    };

    const save = async () => {
        setSaving(true);
        setError("");
        try {
            const cls = classOptions.find((item) => item.id === form.classId);
            await axios.post(`${getApiBaseUrl()}/TimetableSubstitution`, {
                schoolId,
                educationLevel,
                academicYear,
                ...form,
                className: cls?.sclassName || form.className,
                createdBy: "admin",
            });
            setOpen(false);
            onRefresh?.();
        } catch (err) {
            setError(err.response?.data?.message || "Could not save substitution.");
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Remove this substitution?")) return;
        try {
            await axios.delete(`${getApiBaseUrl()}/TimetableSubstitution/${id}`);
            onRefresh?.();
        } catch (err) {
            setError(err.response?.data?.message || "Could not delete substitution.");
        }
    };

    return (
        <Stack spacing={2}>
            {error ? (
                <Alert severity="error" onClose={() => setError("")}>
                    {error}
                </Alert>
            ) : null}
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                    Schedule cover teachers for specific dates when staff are on leave.
                </Typography>
                <Button startIcon={<AddOutlinedIcon />} variant="contained" onClick={openCreate}>
                    Add substitution
                </Button>
            </Stack>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Class</TableCell>
                            <TableCell>Day / period</TableCell>
                            <TableCell>Subject</TableCell>
                            <TableCell>Cover teacher</TableCell>
                            <TableCell>Original</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!substitutions.length ? (
                            <TableRow>
                                <TableCell colSpan={7}>
                                    <Typography variant="body2" color="text.secondary">
                                        No substitutions scheduled.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            substitutions.map((row) => (
                                <TableRow key={row.id || row._id}>
                                    <TableCell>{String(row.effectiveDate || "").slice(0, 10)}</TableCell>
                                    <TableCell>{row.className}</TableCell>
                                    <TableCell>
                                        {row.dayOfWeek} · {row.periodKey}
                                    </TableCell>
                                    <TableCell>{row.subjectName || "—"}</TableCell>
                                    <TableCell>{row.substituteTeacher?.name || row.substituteTeacherId}</TableCell>
                                    <TableCell>{row.originalTeacherName || "—"}</TableCell>
                                    <TableCell align="right">
                                        <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} onClick={() => remove(row.id || row._id)}>
                                            Remove
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Teacher substitution</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Class</InputLabel>
                            <Select
                                label="Class"
                                value={form.classId}
                                onChange={(e) => setForm((prev) => ({ ...prev, classId: e.target.value }))}
                            >
                                {classOptions.map((item) => (
                                    <MenuItem key={item.id} value={item.id}>
                                        {item.sclassName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            size="small"
                            label="Effective date"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            value={form.effectiveDate}
                            onChange={(e) => setForm((prev) => ({ ...prev, effectiveDate: e.target.value }))}
                        />
                        <FormControl fullWidth size="small">
                            <InputLabel>Day of week</InputLabel>
                            <Select
                                label="Day of week"
                                value={form.dayOfWeek}
                                onChange={(e) => setForm((prev) => ({ ...prev, dayOfWeek: e.target.value }))}
                            >
                                {WEEK_DAYS.map((day) => (
                                    <MenuItem key={day} value={day}>
                                        {day}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small">
                            <InputLabel>Period</InputLabel>
                            <Select
                                label="Period"
                                value={form.periodKey}
                                onChange={(e) => setForm((prev) => ({ ...prev, periodKey: e.target.value }))}
                            >
                                {periodOptions.map((item) => (
                                    <MenuItem key={item.key} value={item.key}>
                                        {item.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            size="small"
                            label="Subject"
                            value={form.subjectName}
                            onChange={(e) => setForm((prev) => ({ ...prev, subjectName: e.target.value }))}
                        />
                        <FormControl fullWidth size="small">
                            <InputLabel>Substitute teacher</InputLabel>
                            <Select
                                label="Substitute teacher"
                                value={form.substituteTeacherId}
                                onChange={(e) => setForm((prev) => ({ ...prev, substituteTeacherId: e.target.value }))}
                            >
                                {teacherOptions.map((teacher) => (
                                    <MenuItem key={teacher.id || teacher._id} value={teacher.id || teacher._id}>
                                        {teacher.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            size="small"
                            label="Original teacher (optional)"
                            value={form.originalTeacherName}
                            onChange={(e) => setForm((prev) => ({ ...prev, originalTeacherName: e.target.value }))}
                        />
                        <FormControl fullWidth size="small">
                            <InputLabel>Room (optional)</InputLabel>
                            <Select
                                label="Room (optional)"
                                value={form.facilityId}
                                onChange={(e) => {
                                    const facility = facilityOptions.find((item) => (item.id || item._id) === e.target.value);
                                    setForm((prev) => ({
                                        ...prev,
                                        facilityId: e.target.value,
                                        roomName: facility?.name || "",
                                    }));
                                }}
                            >
                                <MenuItem value="">
                                    <em>—</em>
                                </MenuItem>
                                {facilityOptions.map((item) => (
                                    <MenuItem key={item.id || item._id} value={item.id || item._id}>
                                        {item.name} ({item.facilityType})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            size="small"
                            label="Notes"
                            multiline
                            minRows={2}
                            value={form.notes}
                            onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={save} disabled={saving}>
                        {saving ? "Saving…" : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}

const emptyBookingForm = {
    facilityId: "",
    title: "",
    dayOfWeek: "Monday",
    periodKey: "",
    startTime: "",
    endTime: "",
    classId: "",
    className: "",
    purpose: "",
    bookedBy: "",
    notes: "",
};

export function RoomBookingsPanel({
    schoolId,
    educationLevel,
    academicYear,
    roomBookings = [],
    classOptions = [],
    facilityOptions = [],
    periodOptions = [],
    onRefresh,
}) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(emptyBookingForm);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const openCreate = () => {
        setForm({ ...emptyBookingForm, classId: classOptions[0]?.id || "" });
        setError("");
        setOpen(true);
    };

    const save = async () => {
        setSaving(true);
        setError("");
        try {
            const cls = classOptions.find((item) => item.id === form.classId);
            const period = periodOptions.find((item) => item.key === form.periodKey);
            await axios.post(`${getApiBaseUrl()}/RoomBooking`, {
                schoolId,
                educationLevel,
                academicYear,
                ...form,
                className: cls?.sclassName || form.className,
                startTime: form.startTime || period?.startTime || "",
                endTime: form.endTime || period?.endTime || "",
            });
            setOpen(false);
            onRefresh?.();
        } catch (err) {
            setError(err.response?.data?.message || "Could not save room booking.");
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Remove this room booking?")) return;
        try {
            await axios.delete(`${getApiBaseUrl()}/RoomBooking/${id}`);
            onRefresh?.();
        } catch (err) {
            setError(err.response?.data?.message || "Could not delete booking.");
        }
    };

    return (
        <Stack spacing={2}>
            {error ? (
                <Alert severity="error" onClose={() => setError("")}>
                    {error}
                </Alert>
            ) : null}
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                    Reserve classrooms and labs for recurring weekly periods.
                </Typography>
                <Button startIcon={<AddOutlinedIcon />} variant="contained" onClick={openCreate}>
                    Book room
                </Button>
            </Stack>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Room</TableCell>
                            <TableCell>Day / period</TableCell>
                            <TableCell>Time</TableCell>
                            <TableCell>Purpose</TableCell>
                            <TableCell>Class</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!roomBookings.length ? (
                            <TableRow>
                                <TableCell colSpan={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        No room bookings yet.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            roomBookings.map((row) => (
                                <TableRow key={row.id || row._id}>
                                    <TableCell>{row.facilityName || row.title}</TableCell>
                                    <TableCell>
                                        {row.dayOfWeek} · {row.periodKey}
                                    </TableCell>
                                    <TableCell>
                                        {row.startTime && row.endTime ? `${row.startTime} – ${row.endTime}` : "—"}
                                    </TableCell>
                                    <TableCell>{row.purpose || row.title || "—"}</TableCell>
                                    <TableCell>{row.className || "—"}</TableCell>
                                    <TableCell align="right">
                                        <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} onClick={() => remove(row.id || row._id)}>
                                            Remove
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Room booking</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Facility / room</InputLabel>
                            <Select
                                label="Facility / room"
                                value={form.facilityId}
                                onChange={(e) => {
                                    const facility = facilityOptions.find((item) => (item.id || item._id) === e.target.value);
                                    setForm((prev) => ({
                                        ...prev,
                                        facilityId: e.target.value,
                                        title: facility?.name || prev.title,
                                    }));
                                }}
                            >
                                {facilityOptions.map((item) => (
                                    <MenuItem key={item.id || item._id} value={item.id || item._id}>
                                        {item.name} ({item.facilityType})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small">
                            <InputLabel>Day</InputLabel>
                            <Select label="Day" value={form.dayOfWeek} onChange={(e) => setForm((prev) => ({ ...prev, dayOfWeek: e.target.value }))}>
                                {WEEK_DAYS.map((day) => (
                                    <MenuItem key={day} value={day}>
                                        {day}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small">
                            <InputLabel>Period</InputLabel>
                            <Select
                                label="Period"
                                value={form.periodKey}
                                onChange={(e) => {
                                    const period = periodOptions.find((item) => item.key === e.target.value);
                                    setForm((prev) => ({
                                        ...prev,
                                        periodKey: e.target.value,
                                        startTime: period?.startTime || "",
                                        endTime: period?.endTime || "",
                                    }));
                                }}
                            >
                                {periodOptions.map((item) => (
                                    <MenuItem key={item.key} value={item.key}>
                                        {item.label} ({item.startTime}–{item.endTime})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField size="small" label="Purpose" value={form.purpose} onChange={(e) => setForm((prev) => ({ ...prev, purpose: e.target.value }))} />
                        <FormControl fullWidth size="small">
                            <InputLabel>Class (optional)</InputLabel>
                            <Select label="Class (optional)" value={form.classId} onChange={(e) => setForm((prev) => ({ ...prev, classId: e.target.value }))}>
                                <MenuItem value="">
                                    <em>—</em>
                                </MenuItem>
                                {classOptions.map((item) => (
                                    <MenuItem key={item.id} value={item.id}>
                                        {item.sclassName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField size="small" label="Booked by" value={form.bookedBy} onChange={(e) => setForm((prev) => ({ ...prev, bookedBy: e.target.value }))} />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={save} disabled={saving}>
                        {saving ? "Saving…" : "Save booking"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}
